<template>
  <main class="dashboard-shell" v-if="!loading">
    <header class="dashboard-header">
      <div>
        <p class="date-label">{{ todayLabel }}</p>
        <h1>{{ greeting }}, {{ userName }}</h1>
        <p class="subtle">Here is how your receptionist is performing today.</p>
      </div>
      <div class="header-actions">
        <button class="button button-secondary" type="button" @click="showExportModal = true">
          <Download />
          Export data
        </button>
        <button class="button button-quiet" type="button" @click="logout" :disabled="logoutLoading">
          <span v-if="logoutLoading" class="spinner"></span>
          <LogOut v-else />
          Log out
        </button>
      </div>
    </header>

    <section v-if="visibleSuggestions.length" class="panel suggestions-panel">
      <div class="section-heading">
        <div>
          <h2>Performance suggestions</h2>
          <p>Ways to improve appointment availability and caller outcomes</p>
        </div>
      </div>
      <router-link v-for="suggestion in visibleSuggestions" :key="suggestion.id" :to="suggestion.link"
        class="suggestion-row">
        <span class="suggestion-icon">
          <Lightbulb />
        </span>
        <span class="suggestion-copy">
          <strong>{{ suggestion.title }}</strong>
          <span>{{ suggestion.message }}</span>
          <span class="review-link">Review setting
            <ArrowRight />
          </span>
        </span>
        <button class="icon-button dismiss-button" type="button" title="Dismiss suggestion"
          @click.prevent.stop="dismissSuggestion(suggestion.id)">
          <X />
        </button>
      </router-link>
    </section>

    <section class="panel overview-panel">
      <div class="section-heading overview-heading">
        <div>
          <h2>7-day overview</h2>
          <p>Activity compared with the previous seven days</p>
        </div>
        <span>{{ summaryRangeLabel }}</span>
      </div>
      <div class="overview-grid">
        <article v-for="metric in summaryMetrics" :key="metric.key" class="overview-card">
          <div class="metric-header">
            <div class="metric-label">
              <component :is="metric.icon" /> {{ metric.label }}
            </div>
            <span class="change-pill" :class="changeClass(metric.change, metric.lowerIsBetter)">
              {{ formatChange(metric.change) }}
            </span>
          </div>
          <div class="overview-value-row">
            <div>
              <span class="overview-value">{{ metric.value }}</span>
              <span class="comparison">{{ metric.series[metric.series.length - 1]?.value }} today</span>
            </div>
            <div class="sparkline">
              <CallActivity :call-data="metric.series" value-key="value" :label="metric.label" compact />
            </div>
          </div>
        </article>
      </div>
    </section>

    <section class="panel inbox-panel">
      <div class="section-heading inbox-heading">
        <div>
          <div class="title-with-badge">
            <h2>Inbox</h2>
            <span v-if="unreadNotificationsCount" class="new-badge">{{ unreadNotificationsCount }} new</span>
          </div>
          <p>Review, assign and complete incoming work</p>
        </div>
        <label class="archive-toggle">
          <Archive />
          Show archived
          <input v-model="showArchived" type="checkbox" @change="fetchNotifications" />
          <span class="toggle-track"><span></span></span>
        </label>
      </div>

      <div class="inbox-table" :class="{ busy: notificationsLoading }">
        <div class="inbox-table-header">
          <span>Type</span><span>Request</span><span>Assigned
            to</span><span>Status</span><span>Received</span><span>Actions</span>
        </div>
        <div v-for="notification in displayedNotifications" :key="notification._id" class="inbox-row"
          :class="{ unread: !notification.read, complete: notification.completed }" @click="markAsRead(notification)">
          <div><span class="type-pill" :class="notification.type">
              <component :is="notificationIcon(notification)" />{{ notificationType(notification) }}
            </span></div>
          <div class="request-cell">
            <strong>{{ notification.title }}</strong>
            <span class="notification-message" :class="{ expanded: isMessageExpanded(notification) }">{{ notification.message }}</span>
            <button v-if="isLongNotificationMessage(notification)" class="message-toggle" type="button"
              :aria-expanded="isMessageExpanded(notification)" @click.stop="toggleMessage(notification)">
              {{ isMessageExpanded(notification) ? 'Show less' : 'View full message' }}
              <ChevronUp v-if="isMessageExpanded(notification)" />
              <ChevronDown v-else />
            </button>
            <small v-if="notification.completedBy">Completed by {{ notification.completedBy.name ||
              notification.completedBy.email }}{{ notification.completedAt ? ` ${getTimeAgo(notification.completedAt)}`
              : '' }}</small>
          </div>
          <div @click.stop>
            <select v-if="notification.type === 'human_request'" class="assignee-select"
              :value="notification.assignedTo?.userId || ''" aria-label="Assign notification"
              @change="assignNotification(notification, $event.target.value)">
              <option value="">Unassigned</option>
              <option v-for="assignee in assignees" :key="assignee.userId" :value="assignee.userId">{{ assignee.name ||
                assignee.email }}</option>
            </select>
            <span v-else class="empty-value">-</span>
          </div>
          <div><span class="status-label" :class="notificationStatus(notification).toLowerCase()">{{
            notificationStatus(notification) }}</span></div>
          <time :datetime="notification.date">{{ getTimeAgo(notification.date) }}</time>
          <div class="row-actions" @click.stop>
            <button v-if="notification.type === 'human_request'" class="complete-button"
              :class="{ completed: notification.completed }" type="button"
              :title="notification.completed ? 'Reopen task' : 'Mark task complete'"
              @click="toggleCompleted(notification)">
              <Check /> {{ notification.completed ? 'Reopen' : 'Complete' }}
            </button>
            <button class="icon-button" type="button"
              :title="notification.read ? 'Mark as unread' : 'Mark as read'"
              :aria-label="notification.read ? 'Mark as unread' : 'Mark as read'"
              @click="toggleRead(notification)">
              <Mail v-if="notification.read" />
              <MailOpen v-else />
            </button>
            <button class="icon-button" type="button"
              :title="showArchived ? 'Restore notification' : 'Archive notification'"
              @click="archiveNotification(notification)">
              <RotateCcw v-if="showArchived" />
              <Archive v-else />
            </button>
          </div>
        </div>
        <div v-if="!notifications.length && !notificationsLoading" class="empty-state">
          <Inbox />
          <strong>{{ showArchived ? 'No archived notifications' : 'Inbox clear' }}</strong>
          <span>{{ showArchived ? 'Archived notifications will appear here.' : 'There is no incoming work to review.'
            }}</span>
        </div>
        <div v-if="hasMoreNotifications" class="inbox-pagination">
          <button class="button button-secondary" type="button" @click="notificationDisplayLimit += 10">
            View more
          </button>
        </div>
      </div>
    </section>

    <section class="panel statistics-panel">
      <div class="section-heading statistics-heading">
        <div>
          <h2>Performance statistics</h2>
          <p>Select a metric to inspect its trend over time</p>
        </div>
        <div class="statistics-controls">
          <div class="filters">
            <select v-model="selectedTimeRange" aria-label="Statistics time range" @change="handleTimeRangeChange">
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="custom">Custom range</option>
            </select>
            <select v-model="selectedMethod" aria-label="Statistics channel" @change="fetchStatistics">
              <option value="">All channels</option>
              <option value="voice">Voice</option>
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="email">Email</option>
              <option value="widget">Widget</option>
            </select>
          </div>
          <div v-if="selectedTimeRange === 'custom'" class="custom-range-controls">
            <label>From <input v-model="customStartDate" type="date" :max="customEndDate || today" /></label>
            <label>To <input v-model="customEndDate" type="date" :min="customStartDate" :max="today" /></label>
            <button class="button button-secondary" type="button" @click="applyCustomRange">Apply</button>
          </div>
          <span v-if="customRangeError" class="range-error">{{ customRangeError }}</span>
        </div>
      </div>

      <div class="statistics-grid" :class="{ busy: statisticsLoading }">
        <button v-for="metric in performanceMetrics" :key="metric.key" class="stat-card"
          :class="{ active: expandedMetric === metric.key }" type="button" @click="toggleMetric(metric.key)">
          <span class="stat-card-header"><span>{{ metric.label }}</span><span class="stat-icon">
              <component :is="metric.icon" />
            </span></span>
          <strong>{{ metric.displayValue }}</strong>
          <span class="stat-card-footer">
            <span class="change-pill" :class="changeClass(metric.change, metric.lowerIsBetter)">{{
              formatChange(metric.change) }}</span>
            <ChevronUp v-if="expandedMetric === metric.key" />
            <ChevronDown v-else />
          </span>
        </button>
      </div>

      <div v-if="expandedMetricDefinition" class="expanded-chart">
        <div class="expanded-chart-header">
          <div>
            <h3>{{ expandedMetricDefinition.label }} trend</h3>
            <p>Activity for the selected reporting period and channel</p>
          </div>
          <button class="button button-quiet" type="button" @click="expandedMetric = null">Close</button>
        </div>
        <CallActivity :call-data="expandedSeries" value-key="value"
          :label="expandedMetricDefinition.chartLabel || expandedMetricDefinition.label" />
      </div>
    </section>

    <section class="panel recent-panel">
      <div class="section-heading recent-heading">
        <div>
          <h2>Recent calls</h2>
          <p>Latest inbound conversations</p>
        </div>
        <router-link to="/calls" class="view-all-link">View all
          <ArrowRight />
        </router-link>
      </div>
      <div class="recent-list">
        <button v-for="call in recentCalls.slice(0, 5)" :key="call.id" class="recent-row" type="button"
          @click="$router.push(`/calls?call=${call.id}`)">
          <span class="caller-avatar">{{ initials(call.user_name) }}</span>
          <span class="caller-copy"><strong>{{ call.user_name || 'Unknown caller' }}</strong><span>{{ call.reason ||
              'Call conversation' }}</span></span>
          <span class="call-meta"><span>
              <PhoneIncoming /> {{ formatCallNumber(call.user_number) }}
            </span><span>
              <Clock3 /> {{ formatTime(call.duration) }}
            </span><time>{{ getTimeAgo(call.date) }}</time></span>
        </button>
        <div v-if="!recentCalls.length" class="empty-state compact-empty">
          <Phone /> <span>No recent calls</span>
        </div>
      </div>
    </section>

    <ExportModal :show="showExportModal" @close="showExportModal = false" />
  </main>
  <div v-else class="dashboard-loading"><span class="spinner"></span></div>
</template>

<script>
import CallActivity from '@/components/CallActivity.vue';
import ExportModal from '@/components/ExportModal.vue';
import { getDashboard, getNotifications, getStatistics, getPerformanceSuggestions, updateNotification } from '@/app/admin-routes';
import authService from '@/utils/auth';
import {
  Archive, ArrowRight, CalendarCheck, CalendarClock, CalendarX, Check, ChevronDown, ChevronUp,
  CircleAlert, Clock3, Download, Inbox, Lightbulb, LogOut, Mail, MailOpen, MessageSquare, MessagesSquare,
  Phone, PhoneIncoming, Reply, RotateCcw, Send, Timer, Users, X,
} from 'lucide-vue-next';

const SUMMARY_DEFINITIONS = [
  { key: 'callsReceived', label: 'Calls', icon: Phone },
  { key: 'messagesReceived', label: 'Messages', icon: MessageSquare },
  { key: 'bookingsMade', label: 'Bookings', icon: CalendarCheck },
  { key: 'changed', label: 'Changed', icon: CalendarClock, lowerIsBetter: true },
];

const PERFORMANCE_DEFINITIONS = [
  { key: 'callsReceived', label: 'Calls', icon: Phone },
  { key: 'messagesReceived', label: 'Messages', icon: MessageSquare },
  { key: 'avgCallDuration', label: 'Average call time', chartLabel: 'Average call time (seconds)', icon: Timer, format: 'duration' },
  { key: 'uniqueCallers', label: 'Unique callers', icon: Users },
  { key: 'bookingsMade', label: 'Bookings', icon: CalendarCheck },
  { key: 'reschedulesMade', label: 'Reschedules', icon: CalendarClock, lowerIsBetter: true },
  { key: 'cancellationsMade', label: 'Cancellations', icon: CalendarX, lowerIsBetter: true },
  { key: 'escalations', label: 'Escalations', icon: CircleAlert, lowerIsBetter: true },
  { key: 'totalConversations', label: 'Message conversations', icon: MessagesSquare },
  { key: 'callsOutsideHours', label: 'Calls out of hours', icon: Clock3 },
  { key: 'followUpsSent', label: 'Follow-ups sent', icon: Send },
  { key: 'followUpsReplied', label: 'Follow-ups replied', icon: Reply },
];

export default {
  name: 'DashboardModern',
  components: {
    Archive, ArrowRight, CallActivity, Check, ChevronDown, ChevronUp, Clock3, Download, ExportModal,
    Inbox, Lightbulb, LogOut, Mail, MailOpen, Phone, PhoneIncoming, RotateCcw, Timer, Users, X,
  },
  data() {
    return {
      loading: true,
      logoutLoading: false,
      notificationsLoading: false,
      statisticsLoading: false,
      showExportModal: false,
      showArchived: false,
      performanceSuggestions: [],
      dismissedSuggestions: JSON.parse(sessionStorage.getItem('dismissedDashboardSuggestions') || '[]'),
      notifications: [],
      notificationDisplayLimit: 5,
      expandedNotificationIds: [],
      assignees: [],
      recentCalls: [],
      summaryStats: {},
      summaryPreviousStats: {},
      summarySeries: [],
      currentStats: {},
      previousStats: {},
      performanceSeries: [],
      selectedTimeRange: '30',
      selectedMethod: '',
      customStartDate: '',
      customEndDate: '',
      customRangeError: '',
      today: new Date().toISOString().split('T')[0],
      expandedMetric: 'callsReceived',
      statisticsRequest: 0,
    };
  },
  computed: {
    userName() {
      return localStorage.getItem('name')?.split(' ')[0] || 'there';
    },
    greeting() {
      const hour = new Date().getHours();
      if (hour < 12) return 'Good morning';
      if (hour < 18) return 'Good afternoon';
      return 'Good evening';
    },
    todayLabel() {
      return new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date());
    },
    visibleSuggestions() {
      return this.performanceSuggestions.filter(item => !this.dismissedSuggestions.includes(item.id));
    },
    unreadNotificationsCount() {
      return this.notifications.filter(notification => !notification.read).length;
    },
    displayedNotifications() {
      return this.notifications.slice(0, this.notificationDisplayLimit);
    },
    hasMoreNotifications() {
      return this.notificationDisplayLimit < this.notifications.length;
    },
    summaryRangeLabel() {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 6);
      const formatter = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
      return `${formatter.format(start)} - ${formatter.format(end)}`;
    },
    summaryMetrics() {
      return SUMMARY_DEFINITIONS.map(definition => ({
        ...definition,
        value: this.metricValue(this.summaryStats, definition.key),
        change: this.metricChange(definition.key, this.summaryStats, this.summaryPreviousStats),
        series: this.summarySeries.map(row => ({ date: this.shortDate(row.date), value: this.seriesValue(row, definition.key) })),
      }));
    },
    performanceMetrics() {
      return PERFORMANCE_DEFINITIONS.map(definition => ({
        ...definition,
        value: this.metricValue(this.currentStats, definition.key),
        displayValue: definition.format === 'duration'
          ? this.formatTime(this.metricValue(this.currentStats, definition.key))
          : this.metricValue(this.currentStats, definition.key),
        change: this.metricChange(definition.key, this.currentStats, this.previousStats),
      }));
    },
    expandedMetricDefinition() {
      return PERFORMANCE_DEFINITIONS.find(metric => metric.key === this.expandedMetric) || null;
    },
    expandedSeries() {
      if (!this.expandedMetric) return [];
      return this.performanceSeries.map(row => ({ date: this.shortDate(row.date), value: this.seriesValue(row, this.expandedMetric) }));
    },
  },
  mounted() {
    this.loading = false;
    this.fetchDashboard();
    this.fetchSummary();
    this.fetchNotifications();
    this.fetchStatistics();
    this.fetchSuggestions();
  },
  methods: {
    dateOnly(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    },
    dateRange(days, offset = 0) {
      const end = new Date();
      end.setDate(end.getDate() - offset);
      const start = new Date(end);
      start.setDate(start.getDate() - days + 1);
      return { start: this.dateOnly(start), end: this.dateOnly(end) };
    },
    async fetchDashboard() {
      try {
        const response = await getDashboard();
        this.recentCalls = response?.recentCalls || [];
      } catch (error) {
        console.error('Error fetching recent calls:', error);
      }
    },
    async fetchSummary() {
      try {
        const current = this.dateRange(7);
        const previous = this.dateRange(7, 7);
        const [currentResponse, previousResponse] = await Promise.all([
          getStatistics(current.start, current.end),
          getStatistics(previous.start, previous.end),
        ]);
        this.summaryStats = currentResponse.statistics || {};
        this.summaryPreviousStats = previousResponse.statistics || {};
        this.summarySeries = currentResponse.timeSeries || [];
      } catch (error) {
        console.error('Error fetching dashboard overview:', error);
      }
    },
    async fetchStatistics() {
      const request = ++this.statisticsRequest;
      this.statisticsLoading = true;
      try {
        const ranges = this.statisticsDateRanges();
        if (!ranges) return;
        const { current, previous } = ranges;
        const [currentResponse, previousResponse] = await Promise.all([
          getStatistics(current.start, current.end, this.selectedMethod),
          getStatistics(previous.start, previous.end, this.selectedMethod),
        ]);
        if (request !== this.statisticsRequest) return;
        this.currentStats = currentResponse.statistics || {};
        this.previousStats = previousResponse.statistics || {};
        this.performanceSeries = currentResponse.timeSeries || [];
      } catch (error) {
        console.error('Error fetching performance statistics:', error);
      } finally {
        if (request === this.statisticsRequest) this.statisticsLoading = false;
      }
    },
    handleTimeRangeChange() {
      this.customRangeError = '';
      if (this.selectedTimeRange === 'custom') {
        if (!this.customStartDate || !this.customEndDate) {
          const defaultRange = this.dateRange(30);
          this.customStartDate = defaultRange.start;
          this.customEndDate = defaultRange.end;
        }
        return;
      }
      this.fetchStatistics();
    },
    statisticsDateRanges() {
      if (this.selectedTimeRange !== 'custom') {
        const days = Number(this.selectedTimeRange);
        return { current: this.dateRange(days), previous: this.dateRange(days, days) };
      }

      if (!this.customStartDate || !this.customEndDate || this.customStartDate > this.customEndDate) return null;
      const currentStart = new Date(`${this.customStartDate}T00:00:00`);
      const currentEnd = new Date(`${this.customEndDate}T00:00:00`);
      const durationDays = Math.round((currentEnd - currentStart) / 86400000) + 1;
      const previousEnd = new Date(currentStart);
      previousEnd.setDate(previousEnd.getDate() - 1);
      const previousStart = new Date(previousEnd);
      previousStart.setDate(previousStart.getDate() - durationDays + 1);
      return {
        current: { start: this.customStartDate, end: this.customEndDate },
        previous: { start: this.dateOnly(previousStart), end: this.dateOnly(previousEnd) },
      };
    },
    applyCustomRange() {
      if (!this.customStartDate || !this.customEndDate) {
        this.customRangeError = 'Choose both a start and end date.';
        return;
      }
      if (this.customStartDate > this.customEndDate) {
        this.customRangeError = 'Start date must be before the end date.';
        return;
      }
      this.customRangeError = '';
      this.fetchStatistics();
    },
    async fetchNotifications() {
      this.notificationDisplayLimit = 5;
      this.expandedNotificationIds = [];
      this.notificationsLoading = true;
      try {
        const response = await getNotifications(this.showArchived);
        if (response.success) {
          this.notifications = response.notifications || [];
          this.assignees = response.assignees || [];
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        this.notificationsLoading = false;
      }
    },
    async fetchSuggestions() {
      try {
        const response = await getPerformanceSuggestions();
        this.performanceSuggestions = response.suggestions || [];
      } catch (error) {
        console.error('Error fetching performance suggestions:', error);
      }
    },
    dismissSuggestion(id) {
      this.dismissedSuggestions.push(id);
      sessionStorage.setItem('dismissedDashboardSuggestions', JSON.stringify(this.dismissedSuggestions));
    },
    async applyNotificationUpdate(notification, updates) {
      const response = await updateNotification(notification._id, updates);
      if (!response.success) return;
      const index = this.notifications.findIndex(item => item._id === notification._id);
      if (index !== -1) this.notifications.splice(index, 1, response.notification);
    },
    markAsRead(notification) {
      if (!notification.read) this.applyNotificationUpdate(notification, { read: true });
    },
    toggleRead(notification) {
      this.applyNotificationUpdate(notification, { read: !notification.read });
    },
    isLongNotificationMessage(notification) {
      return (notification.message || '').length > 60;
    },
    isMessageExpanded(notification) {
      return this.expandedNotificationIds.includes(notification._id);
    },
    toggleMessage(notification) {
      if (this.isMessageExpanded(notification)) {
        this.expandedNotificationIds = this.expandedNotificationIds.filter(id => id !== notification._id);
      } else {
        this.expandedNotificationIds.push(notification._id);
        this.markAsRead(notification);
      }
    },
    assignNotification(notification, assignedToId) {
      this.applyNotificationUpdate(notification, { assignedToId });
    },
    toggleCompleted(notification) {
      this.applyNotificationUpdate(notification, { completed: !notification.completed });
    },
    async archiveNotification(notification) {
      const response = await updateNotification(notification._id, { archived: !this.showArchived });
      if (response.success) this.notifications = this.notifications.filter(item => item._id !== notification._id);
    },
    metricValue(stats, key) {
      if (key === 'changed') return (stats.reschedulesMade || 0) + (stats.cancellationsMade || 0);
      return stats[key] || 0;
    },
    seriesValue(row, key) {
      if (key === 'changed') return (row.reschedulesMade || 0) + (row.cancellationsMade || 0);
      return row[key] || 0;
    },
    metricChange(key, current, previous) {
      const currentValue = this.metricValue(current, key);
      const previousValue = this.metricValue(previous, key);
      if (!previousValue) return currentValue ? 100 : 0;
      return ((currentValue - previousValue) / previousValue) * 100;
    },
    changeClass(change, lowerIsBetter) {
      if (!change) return 'neutral';
      const positive = lowerIsBetter ? change < 0 : change > 0;
      return positive ? 'positive' : 'negative';
    },
    formatChange(change) {
      const arrow = change > 0 ? '+' : change < 0 ? '-' : '';
      return `${arrow}${Math.abs(change).toFixed(1)}%`;
    },
    shortDate(value) {
      const [year, month, day] = value.split('-').map(Number);
      return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(new Date(year, month - 1, day));
    },
    notificationIcon(notification) {
      if (notification.type === 'human_request') return CircleAlert;
      if (notification.type === 'warning' || notification.type === 'error') return CircleAlert;
      return Mail;
    },
    notificationType(notification) {
      const labels = { human_request: 'Escalation', warning: 'Warning', error: 'Error', success: 'Update', update: 'Update', info: 'Message' };
      return labels[notification.type] || 'Message';
    },
    notificationStatus(notification) {
      if (notification.completed) return 'Done';
      if (notification.type === 'human_request') return notification.read ? 'Open' : 'New';
      return notification.read ? 'Read' : 'New';
    },
    getTimeAgo(value) {
      const seconds = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
      if (seconds < 60) return 'just now';
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes} min ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} hr ago`;
      const days = Math.floor(hours / 24);
      return `${days} day${days === 1 ? '' : 's'} ago`;
    },
    formatTime(seconds) {
      const duration = Number(seconds) || 0;
      const minutes = Math.floor(duration / 60);
      return `${minutes}:${String(Math.floor(duration % 60)).padStart(2, '0')}`;
    },
    formatCallNumber(number) {
      return number ? `+${number}` : 'No number';
    },
    initials(name) {
      if (!name) return '?';
      return name.split(' ').slice(0, 2).map(part => part[0]).join('').toUpperCase();
    },
    toggleMetric(key) {
      this.expandedMetric = this.expandedMetric === key ? null : key;
    },
    async logout() {
      this.logoutLoading = true;
      try {
        await authService.logout();
      } catch (error) {
        authService.clearAuth();
      } finally {
        this.logoutLoading = false;
        this.$router.push('/login');
      }
    },
  },
};
</script>

<style scoped lang="scss">
.dashboard-shell {
  --ink: #0c1b33;
  --muted: #657188;
  --line: #e4e9f1;
  --surface: #ffffff;
  --page: #f5f7fb;
  --accent: #2562be;
  --accent-soft: #eef2ff;
  --green: #087f5b;
  --green-soft: #e8f7f1;
  --red: #d64747;
  --red-soft: #fff0f0;
  min-height: 100%;
  padding: 16px;
  background: var(--page);
  color: var(--ink);
  text-align: left;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
}

.dashboard-header,
.panel {
  width: min(100%, 1400px);
  margin: 0 auto 16px;
  box-sizing: border-box;
}

.dashboard-header {
  min-height: 112px;
  padding: 22px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 8px;
}

.date-label {
  margin: 0 0 4px;
  color: var(--accent);
  font-size: 12px;
  font-weight: 700;
}

h1,
h2,
h3,
p {
  margin-top: 0;
}

h1 {
  margin-bottom: 4px;
  font-size: 30px;
  line-height: 1.2;
  letter-spacing: 0;
  color: var(--ink) !important;
}

h2 {
  margin-bottom: 5px;
  font-size: 20px;
  line-height: 1.3;
  letter-spacing: 0;
}

h3 {
  margin-bottom: 4px;
  font-size: 18px;
  letter-spacing: 0;
}

.subtle,
.section-heading p,
.expanded-chart p {
  margin-bottom: 0;
  color: var(--muted);
  font-size: 14px;
}

.header-actions,
.filters,
.row-actions,
.title-with-badge,
.stat-card-footer {
  display: flex;
  align-items: center;
}

.header-actions {
  gap: 8px;
}

.button {
  min-height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: none;
  color: var(--ink);
  font: inherit;
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
}

.button svg {
  width: 15px;
  height: 15px;
}

.button-secondary {
  border-color: var(--line);
  background: #fafbfc;
}

.button-quiet:hover,
.button-secondary:hover {
  background: #f0f3f8;
}

.button:disabled {
  opacity: .55;
  cursor: wait;
}

.panel {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 8px;
  overflow: hidden;
}

.suggestions-panel {
  background: #f3f8ff;
}

.section-heading {
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  border-bottom: 1px solid var(--line);
}

.suggestion-row {
  position: relative;
  padding: 18px 48px 18px 20px;
  display: flex;
  align-items: flex-start;
  gap: 13px;
  color: inherit;
  text-decoration: none;
}

.suggestion-row+.suggestion-row {
  border-top: 1px solid var(--line);
}

.suggestion-icon {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 6px;
  background: var(--accent);
  color: white;
}

.suggestion-icon svg {
  width: 16px;
}

.suggestion-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.5;
}

.suggestion-copy strong {
  color: var(--ink);
  font-size: 15px;
}

.suggestion-kicker {
  color: var(--accent);
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.review-link {
  margin-top: 3px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--accent);
  font-weight: 700;

  &:hover {
    text-decoration: underline;
  }
}

.review-link svg {
  width: 12px;
}

.dismiss-button {
  position: absolute;
  top: 12px;
  right: 14px;
  background: #f3f8ff !important;

  &:hover {
    background: #e6f0ff !important;
  }
}

.overview-heading>span {
  color: var(--muted);
  font-size: 11px;
}

.overview-grid {
  padding: 16px 20px 20px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.overview-card {
  position: relative;
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: 7px;
  background: rgba(248, 250, 252, 0.5);
  overflow: hidden;
}

.metric-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.metric-label {
  display: flex;
  align-items: center;
  gap: 9px;
  color: var(--muted);
  font-size: 15px;
  font-weight: 500;
}

.metric-label svg {
  width: 18px;
  height: 18px;
  padding: 7px;
  border-radius: 7px;
  background: var(--accent-soft);
  color: var(--accent);
}

.overview-value-row {
  position: relative;
  z-index: 2;
  margin-top: 20px;
  min-height: 60px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.overview-value-row>div {
  display: flex;
  flex-direction: column;
}

.overview-value {
  font-size: 30px;
  font-weight: 750;
  line-height: 1.1;
}

.comparison {
  margin-top: 3px;
  color: var(--muted);
  font-size: 11px;
}

.sparkline {
  position: absolute;
  right: 8px;
  bottom: 9px;
  width: 42%;
  height: 48px;
  z-index: 1;
}

.change-pill {
  width: fit-content;
  padding: 3px 6px;
  border-radius: 10px;
  font-size: 9px;
  font-weight: 750;
}

.change-pill.positive {
  color: var(--green);
  background: var(--green-soft);
}

.change-pill.negative {
  color: var(--red);
  background: var(--red-soft);
}

.change-pill.neutral {
  color: var(--muted);
  background: #f0f2f6;
}

.title-with-badge {
  gap: 7px;
}

.new-badge {
  padding: 3px 7px;
  border-radius: 10px;
  background: var(--accent);
  color: white;
  font-size: 9px;
  font-weight: 750;
}

.archive-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #3f4a5d;
  font-size: 13px;
  cursor: pointer;
}

.archive-toggle>svg {
  width: 13px;
}

.archive-toggle input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.toggle-track {
  width: 34px;
  height: 19px;
  padding: 2px;
  border-radius: 12px;
  background: #dfe4ec;
  box-sizing: border-box;
  transition: background .2s;
}

.toggle-track span {
  display: block;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 3px rgba(12, 27, 51, .22);
  transition: transform .2s;
}

.archive-toggle input:checked+.toggle-track {
  background: var(--accent);
}

.archive-toggle input:checked+.toggle-track span {
  transform: translateX(15px);
}

.inbox-table,
.statistics-grid {
  transition: opacity .2s;
}

.busy {
  opacity: .55;
  pointer-events: none;
}

.inbox-table-header,
.inbox-row {
  display: grid;
  grid-template-columns: 130px minmax(240px, 1fr) 155px 85px 100px 150px;
  gap: 12px;
  align-items: center;
}

.inbox-table-header {
  padding: 10px 20px;
  background: #fafbfc;
  border-bottom: 1px solid var(--line);
  color: var(--muted);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.inbox-row {
  min-height: 74px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--line);
  color: var(--muted);
  font-size: 13px;
  cursor: pointer;
}

.inbox-row:last-child {
  border-bottom: 0;
}

.inbox-row:hover {
  background: #fbfcff;
}

.inbox-row.unread .request-cell strong::before {
  content: "";
  width: 5px;
  height: 5px;
  display: inline-block;
  margin-right: 6px;
  border-radius: 50%;
  background: var(--accent);
  vertical-align: middle;
}

.inbox-row.complete {
  background: #fbfcfd;
}

.inbox-pagination {
  padding: 12px 20px;
  display: flex;
  justify-content: center;
  border-top: 1px solid var(--line);
}

.request-cell {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.request-cell strong,
.request-cell span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.request-cell .notification-message.expanded {
  white-space: normal;
  overflow: visible;
  overflow-wrap: anywhere;
}

.message-toggle {
  width: fit-content;
  padding: 2px 0;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border: 0;
  background: none;
  color: var(--accent);
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}

.message-toggle:hover {
  text-decoration: underline;
}

.message-toggle svg {
  width: 12px;
  height: 12px;
}

.request-cell strong {
  color: var(--ink);
  font-size: 13px;
}

.request-cell small {
  color: var(--green);
  font-size: 11px;
}

.type-pill,
.status-label {
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 700;
}

.type-pill {
  padding: 4px 7px;
  border-radius: 10px;
  background: #eef1f5;
  color: #536075;
}

.type-pill svg {
  width: 11px;
  height: 11px;
}

.type-pill.human_request,
.type-pill.warning {
  background: #fff4df;
  color: #96620b;
}

.type-pill.error {
  background: var(--red-soft);
  color: var(--red);
}

.type-pill.success {
  background: var(--green-soft);
  color: var(--green);
}

.status-label.new {
  color: var(--accent);
}

.status-label.open {
  color: #9a6700;
}

.status-label.done,
.status-label.read {
  color: var(--green);
}

.assignee-select,
.filters select {
  height: 32px;
  padding: 0 28px 0 9px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: white;
  color: #354158;
  font: inherit;
  font-size: 11px;
}

.assignee-select {
  max-width: 145px;
}

.empty-value {
  color: #a3adbc;
}

.row-actions {
  justify-content: flex-end;
  gap: 5px;
}

.icon-button {
  width: 30px;
  height: 30px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: white;
  color: #526079;
  cursor: pointer;
}

.icon-button:hover {
  background: #f0f3f8;
  color: var(--accent);
}

.icon-button svg {
  width: 13px;
  height: 13px;
}

.complete-button {
  height: 30px;
  padding: 0 9px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 0;
  border-radius: 6px;
  background: var(--accent);
  color: white;
  font: inherit;
  font-size: 9px;
  font-weight: 500;
  cursor: pointer;
}

.complete-button.completed {
  border: 1px solid var(--line);
  background: white;
  color: var(--muted);
}

.complete-button svg {
  width: 12px;
}

.filters {
  gap: 7px;
}

.statistics-controls {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.custom-range-controls {
  display: flex;
  align-items: flex-end;
  gap: 7px;
}

.custom-range-controls label {
  display: flex;
  flex-direction: column;
  gap: 3px;
  color: var(--muted);
  font-size: 10px;
}

.custom-range-controls input {
  height: 32px;
  padding: 0 8px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: white;
  color: #354158;
  font: inherit;
  font-size: 10px;
  box-sizing: border-box;
}

.range-error {
  color: var(--red);
  font-size: 10px;
}

.statistics-grid {
  padding: 16px 20px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 9px;
}

.stat-card {
  min-height: 108px;
  padding: 13px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid var(--line);
  border-radius: 7px;
  background: white;
  color: var(--ink);
  text-align: left;
  cursor: pointer;
}

.stat-card:hover,
.stat-card.active {
  border-color: #aebcf3;
  box-shadow: 0 2px 8px rgba(49, 87, 213, .08);
}

.stat-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--muted);
  font-size: 13px;
}

.stat-icon {
  width: 25px;
  height: 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: #f2f4f8;
}

.stat-icon svg {
  width: 13px;
}

.stat-card>strong {
  align-self: center;
  font-size: 24px;
}

.stat-card-footer {
  justify-content: space-between;
}

.stat-card-footer>svg {
  width: 12px;
  color: var(--muted);
}

.expanded-chart {
  margin: 0 20px 20px;
  padding: 16px;
  border: 1px solid var(--line);
  border-radius: 7px;
}

.expanded-chart-header {
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.recent-heading {
  padding-bottom: 14px;
}

.view-all-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--ink);
  font-size: 10px;
  font-weight: 700;
  text-decoration: none;
}

.view-all-link svg {
  width: 12px;
}

.recent-row {
  width: 100%;
  min-height: 58px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 11px;
  border: 0;
  border-bottom: 1px solid var(--line);
  background: white;
  color: var(--ink);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.recent-row:last-child {
  border-bottom: 0;
}

.recent-row:hover {
  background: #fbfcff;
}

.caller-avatar {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 50%;
  background: #eef1f6;
  color: #40506b;
  font-size: 13px;
  font-weight: 750;
}

.caller-copy {
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}

.caller-copy strong {
  font-size: 14px;
}

.caller-copy span {
  color: var(--muted);
  font-size: 13px;
}

.call-meta {
  display: flex;
  align-items: center;
  gap: 15px;
  color: var(--muted);
  font-size: 13px;
}

.call-meta>span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.call-meta svg {
  width: 11px;
}

.empty-state {
  min-height: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  color: var(--muted);
  font-size: 13px;
}

.empty-state svg {
  width: 22px;
}

.empty-state strong {
  color: var(--ink);
}

.compact-empty {
  min-height: 76px;
  flex-direction: row;
}

.dashboard-loading {
  min-height: 100vh;
  display: grid;
  place-items: center;
  background: #f5f7fb;
}

.spinner {
  width: 15px;
  height: 15px;
  display: inline-block;
  border: 2px solid #d9dfeb;
  border-top-color: var(--accent, #3157d5);
  border-radius: 50%;
  animation: spin .8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1050px) {
  .overview-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .inbox-table-header {
    display: none;
  }

  .inbox-row {
    grid-template-columns: 110px minmax(220px, 1fr) 140px 70px 90px;
  }

  .inbox-row> :last-child {
    grid-column: 2 / -1;
    justify-content: flex-start;
  }
}

@media (max-width: 760px) {
  .dashboard-shell {
    padding: 10px 10px 80px;
  }

  .dashboard-header {
    padding: 18px;
    align-items: flex-start;
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
  }

  .header-actions .button {
    flex: 1;
  }

  h1 {
    font-size: 22px;
  }

  .section-heading {
    padding: 15px;
    align-items: flex-start;
    flex-direction: column;
  }

  .overview-heading>span {
    display: none;
  }

  .overview-grid,
  .statistics-grid {
    padding: 12px;
    grid-template-columns: 1fr;
  }

  .suggestion-row {
    padding-left: 15px;
  }

  .inbox-row {
    padding: 14px 15px;
    grid-template-columns: 1fr 1fr;
    gap: 9px;
  }

  .inbox-row> :nth-child(2) {
    grid-column: 1 / -1;
    grid-row: 1;
  }

  .inbox-row> :first-child {
    grid-row: 2;
  }

  .inbox-row> :nth-child(3) {
    grid-row: 2;
    justify-self: end;
  }

  .inbox-row> :nth-child(4) {
    grid-row: 3;
  }

  .inbox-row> :nth-child(5) {
    grid-row: 3;
    justify-self: end;
  }

  .inbox-row> :last-child {
    grid-column: 1 / -1;
    grid-row: 4;
  }

  .filters {
    width: 100%;
  }

  .statistics-controls {
    width: 100%;
    align-items: stretch;
  }

  .custom-range-controls {
    align-items: stretch;
    flex-direction: column;
  }

  .filters select {
    min-width: 0;
    flex: 1;
  }

  .expanded-chart {
    margin: 0 12px 12px;
    padding: 12px;
  }

  .recent-row {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .call-meta {
    width: 100%;
    padding-left: 41px;
    justify-content: space-between;
    gap: 7px;
  }
}
</style>